import type { Session } from "./schemas/session.js";
import { sessionDurationMin } from "./duration.js";
import { formatDuration } from "./sessionSummaryMath.js";

// Pure grouping + label helpers for the Workouts history list. Kept free of
// native imports so they're unit-testable and shared by the mobile screen and
// the web-rnw console (the WorkoutsView lift, @heraion/ui). The screen
// keeps data loading, collapse state and render; this owns the month-section
// build and the locale-bound formatters.

// Intl.DateTimeFormat is costly to construct, and formatDayBadge runs once per
// list ROW on every render (and monthLabel per section header). Constructing a
// fresh formatter each call showed up as scroll jank on long histories. Cache
// one formatter per (locale, shape) so the list scrolls without rebuilding them
// (perf A1, 2026-06-02). The cache is unbounded but the key space is tiny
// (≤ ~5 locales × 3 shapes).
const fmtCache = new Map<string, Intl.DateTimeFormat>();
function dtf(
  locale: string,
  shape: string,
  opts: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormat {
  const key = `${locale}|${shape}`;
  let f = fmtCache.get(key);
  if (!f) {
    f = new Intl.DateTimeFormat(locale, opts);
    fmtCache.set(key, f);
  }
  return f;
}

export type WorkoutMonthSection = {
  key: string;
  total: number;
  data: Session[];
  collapsed: boolean;
};

// Build month sections from the loaded sessions. Key is "YYYY-MM" for
// stable sort; the most recent month auto-expands so the user sees
// their latest sessions on launch, other months collapse by default.
// `collapsedMonths` stores the user's toggle decisions: for the most
// recent month a presence flips it to collapsed; for any older month a
// presence flips it to expanded.
export function buildMonthSections(
  items: Session[],
  collapsedMonths: ReadonlySet<string>,
): WorkoutMonthSection[] {
  const byMonth = new Map<string, Session[]>();
  for (const s of items) {
    const at = new Date(s.startedAt);
    if (Number.isNaN(at.getTime())) continue;
    const key = `${at.getFullYear()}-${String(at.getMonth() + 1).padStart(2, "0")}`;
    const list = byMonth.get(key);
    if (list) list.push(s);
    else byMonth.set(key, [s]);
  }
  const keys = Array.from(byMonth.keys()).sort((a, b) => b.localeCompare(a));
  return keys.map((key, idx) => {
    const list = byMonth.get(key) ?? [];
    const isMostRecent = idx === 0;
    const userToggled = collapsedMonths.has(key);
    const collapsed = isMostRecent ? userToggled : !userToggled;
    return {
      key,
      total: list.length,
      data: collapsed ? [] : list,
      collapsed,
    };
  });
}

export function monthLabel(key: string, locale: string): string {
  const [yearStr, monthStr] = key.split("-");
  const year = Number(yearStr);
  const month = Number(monthStr) - 1;
  if (!Number.isFinite(year) || !Number.isFinite(month)) return key;
  const fmt = dtf(locale, "monthYear", { month: "long", year: "numeric" });
  return fmt.format(new Date(year, month, 1));
}

export function formatDayBadge(
  iso: string,
  locale: string,
): { day: string; dow: string } {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return { day: "—", dow: "" };
  const dayFmt = dtf(locale, "day", { day: "2-digit" });
  const dowFmt = dtf(locale, "dow", { weekday: "short" });
  return {
    day: dayFmt.format(d),
    dow: dowFmt.format(d).toUpperCase(),
  };
}

// "Tue 29": short weekday + two-digit day, in the athlete's language (it:
// "Mar 29"). Used by the auto workout name (#367). The weekday is capitalised
// and loses the abbreviation dot some locales add ("mar." -> "Mar").
export function formatShortDayLabel(iso: string, locale: string): string {
  const { day, dow } = formatDayBadge(iso, locale);
  if (!dow) return "";
  const w = dow.toLowerCase().replace(/\.$/, "");
  // U+00A0: the name never wraps inside its own date.
  return `${w.charAt(0).toUpperCase()}${w.slice(1)}\u00A0${day}`;
}

/** Null when there is no measurable span; formatDuration owns the format. */
export function formatDurationLabel(
  started: string,
  ended: string | undefined,
): string | null {
  const raw = sessionDurationMin(started, ended);
  return raw === null ? null : formatDuration(raw);
}
